// src/features/plan/MarketPressureDrawer.jsx
//
// DRAWER-MARKET-PRESSURE-REWORK: Market Pressure detail drawer reworked to
// shared skeleton pattern.
//
// Extracted from MarketPressurePage.jsx DetailPanel and upgraded to match the
// drawer skeleton established by LocationDrawerV2 and LeaseDrawer.
//
// No photo block, no colored edge stripe — this is a market, not a building.
// All colors from T.* tokens (no raw hex per L52 rule).
//
// Props:
//   market           object    — market row from the pressure API response
//   onOpenMarketPlan function  — called with marketId to open the Market Plan page
//   onTestScenario   function  — called with marketId to open What-If
//   layer            string    — current layer toggle value (for logging only)
//   basis            string    — current basis toggle value (for logging only)
//   horizon          number    — current horizon value (for logging only)
//   onClose          function  — called when the close button is clicked

import React from "react";
import { T } from "../../app/tokens.js";
import { log } from "../../utils/logger.js";

// ── RAG palette — kept in sync with MarketPressurePage.jsx ───────────────────

const RAG_STYLE = {
  over: {
    bg:        T.ragRedBg,
    border:    T.ragRedBorder,
    text:      T.ragRed,
    label:     "Over",
    pillLabel: "Over capacity",
  },
  tight: {
    bg:        T.ragAmberBg,
    border:    T.ragAmberBorder,
    text:      T.ragAmberText,
    label:     "Tight",
    pillLabel: "Tight",
  },
  covered: {
    bg:        T.ragGreenBg,
    border:    T.ragGreenBorder,
    text:      T.ragGreenText,
    label:     "OK",
    pillLabel: "Covered",
  },
  attendance_unknown: {
    bg:        T.lightGray,
    border:    T.border,
    text:      T.mid,
    label:     "No data",
    pillLabel: "No data",
  },
  insufficient_history: {
    bg:        T.lightGray,
    border:    T.border,
    text:      T.light,
    label:     "—",
    pillLabel: "—",
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Format an ISO date string ("YYYY-MM-DD") as "Month YYYY" (e.g. "Jul 2026").
 * Returns null when the input is absent or unparseable.
 */
function fmtAttendanceMonth(monthStr) {
  if (!monthStr) return null;
  const d = new Date(String(monthStr).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** Worst verdict across an office's quarterly cells. */
function worstVerdictForOffice(cells = []) {
  const PRIORITY = ["over", "tight", "covered", "attendance_unknown", "insufficient_history"];
  return cells.reduce((worst, cell) => {
    return PRIORITY.indexOf(cell.verdict) < PRIORITY.indexOf(worst) ? cell.verdict : worst;
  }, "insufficient_history");
}

/**
 * One-line reason string summarising the pressure signal.
 * Format: "{people} people vs {seats} seats · {peak}% peak · {verdict} {scope}"
 */
function buildReasonString(market) {
  const parts = [];
  const quarters = market.quarters ?? [];

  if (market.totalPeople != null && market.totalSeats != null) {
    parts.push(`${market.totalPeople} people vs ${market.totalSeats} seats`);
  }
  if (market.peakAttendPct != null) {
    parts.push(`${market.peakAttendPct}% peak`);
  }

  const badQuarters = quarters.filter((q) => q.verdict === "over" || q.verdict === "tight");
  if (badQuarters.length > 0 && quarters.length > 0) {
    const verdict = badQuarters[0].verdict === "over" ? "over" : "tight";
    if (badQuarters.length === quarters.length) {
      parts.push(`${verdict} every quarter through the horizon`);
    } else {
      parts.push(`${verdict} in ${badQuarters.length} of ${quarters.length} quarters`);
    }
  }

  return parts.join(" · ");
}

/**
 * Label for the "Next wall" KPI tile.
 * Returns "Now" if the first quarter is already over/tight, the quarter label
 * of the first breach, or "None" if all quarters are covered.
 */
export function computeNextWall(quarters) {
  if (!quarters || quarters.length === 0) return "—";
  if (quarters[0]?.verdict === "over" || quarters[0]?.verdict === "tight") return "Now";
  const next = quarters.find((q) => q.verdict === "over" || q.verdict === "tight");
  if (!next) return "None";
  return next.label ?? "—";
}

/**
 * Runway label: months of headroom before the first over/tight quarter.
 * Returns "0 mo" if already breached, "{N}+ mo" if all quarters covered,
 * or a month estimate based on the breach quarter's start date.
 */
export function computeRunway(quarters) {
  if (!quarters || quarters.length === 0) return "—";
  if (quarters[0]?.verdict === "over" || quarters[0]?.verdict === "tight") return "0 mo";

  const idx = quarters.findIndex((q) => q.verdict === "over" || q.verdict === "tight");
  if (idx < 0) {
    return `${quarters.length * 3}+ mo`;
  }

  if (quarters[idx]?.start) {
    const startDate = new Date(String(quarters[idx].start).slice(0, 10) + "T00:00:00");
    if (!isNaN(startDate.getTime())) {
      const months = Math.round((startDate.getTime() - Date.now()) / (30.44 * 86400000));
      return months > 0 ? `${months} mo` : "0 mo";
    }
  }
  return `~${idx * 3} mo`;
}

// ── KPI tile sub-component ────────────────────────────────────────────────────

function KpiTile({ testId, label, value, descriptor }) {
  return (
    <div
      data-testid={testId}
      style={{
        background:    T.lightGray,
        borderRadius:  7,
        padding:       "9px 10px 8px",
        display:       "flex",
        flexDirection: "column",
        gap:           2,
      }}
    >
      <div style={{
        fontSize:      T.fsXxxs5,
        fontWeight:    T.fwBold,
        color:         T.light,
        textTransform: "uppercase",
        letterSpacing: ".07em",
      }}>
        {label}
      </div>
      <div style={{
        fontSize:   T.fsSm5,
        fontWeight: T.fwBold,
        color:      T.ink,
        lineHeight: 1.1,
      }}>
        {value}
      </div>
      {descriptor && (
        <div style={{ fontSize: T.fsXxxs5, color: T.light }}>
          {descriptor}
        </div>
      )}
    </div>
  );
}

/**
 * Format a list of "YYYY-MM" month keys as a human-readable range.
 * e.g. ["2026-01","2026-02","2026-03"] → "Jan–Mar 2026"
 *      ["2025-11","2025-12","2026-01"] → "Nov 2025–Jan 2026"
 */
function formatMonthRange(monthKeys) {
  if (!monthKeys || monthKeys.length === 0) return null;
  const sorted = [...monthKeys].sort();
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const first = new Date(`${sorted[0]}-01T00:00:00.000Z`);
  const last  = new Date(`${sorted[sorted.length - 1]}-01T00:00:00.000Z`);
  if (first.getUTCFullYear() === last.getUTCFullYear()) {
    if (first.getUTCMonth() === last.getUTCMonth()) {
      return `${MONTHS[first.getUTCMonth()]} ${first.getUTCFullYear()}`;
    }
    return `${MONTHS[first.getUTCMonth()]}–${MONTHS[last.getUTCMonth()]} ${first.getUTCFullYear()}`;
  }
  return `${MONTHS[first.getUTCMonth()]} ${first.getUTCFullYear()}–${MONTHS[last.getUTCMonth()]} ${last.getUTCFullYear()}`;
}

// ── Main component ────────────────────────────────────────────────────────────

export function MarketPressureDrawer({
  market,
  onOpenMarketPlan,
  onTestScenario,
  layer,
  basis,
  horizon,
  onClose,
}) {
  if (!market) return null;

  const rollupStyle        = RAG_STYLE[market.rollupVerdict] ?? RAG_STYLE.insufficient_history;
  const hasOneTimeMove     = (market.offices ?? []).some((o) => o.oneTimeMoveDetected);
  const predecessorOffices = market.predecessorOffices ?? [];

  const totalPeople = market.totalPeople;
  const totalSeats  = market.totalSeats;
  const peakPct     = market.peakAttendPct;
  const quarters    = market.quarters ?? [];
  const officeCount = (market.offices ?? []).length;

  const gapVal = (totalSeats != null && totalPeople != null)
    ? totalSeats - totalPeople
    : null;

  return (
    <div
      data-testid="market-detail-panel"
      style={{
        background:    T.card,
        border:        `1px solid ${T.border}`,
        borderRadius:  10,
        display:       "flex",
        flexDirection: "column",
        alignSelf:     "flex-start",
        position:      "sticky",
        top:           16,
        overflow:      "hidden",
        maxHeight:     "calc(100vh - 48px)",
      }}
    >

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{ padding: "16px 18px 14px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>

        {/* Eyebrow + close button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 5 }}>
          <div
            data-testid="detail-panel-eyebrow"
            style={{
              fontSize:      T.fsXs,
              fontWeight:    T.fwSemibold,
              color:         T.mid,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Market
          </div>
          <button
            data-testid="detail-panel-close"
            type="button"
            onClick={onClose}
            aria-label="Close detail panel"
            style={{
              background: "none",
              border:     0,
              cursor:     "pointer",
              color:      T.mid,
              fontSize:   T.fsSm,
              padding:    "2px 4px",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Market name */}
        <div
          data-testid="detail-panel-market-name"
          style={{
            fontSize:     T.fsLg5,
            fontWeight:   T.fwDisplay,
            color:        T.ink,
            lineHeight:   1.2,
            marginBottom: 3,
          }}
        >
          {market.name}
        </div>

        {/* Subtitle: region · office count */}
        <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 11 }}>
          {[
            market.region,
            `${officeCount} office${officeCount !== 1 ? "s" : ""}`,
          ].filter(Boolean).join(" · ")}
        </div>

        {/* Labeled chip group: Pressure → RAG chip */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{
            fontSize:      T.fsXxxs,
            fontWeight:    T.fwBold,
            color:         T.light,
            textTransform: "uppercase",
            letterSpacing: ".06em",
          }}>
            Pressure
          </span>
          <span
            data-testid={`detail-pressure-chip-${market.rollupVerdict}`}
            style={{
              display:      "inline-flex",
              alignItems:   "center",
              padding:      "2px 10px",
              borderRadius: 6,
              fontSize:     T.fsXs,
              fontWeight:   T.fwSemibold,
              background:   rollupStyle.bg,
              color:        rollupStyle.text,
            }}
          >
            {rollupStyle.label}
          </span>
        </div>
      </div>

      {/* ── Scrollable body ─────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: "auto" }}>

        {/* ── Signal section ──────────────────────────────────────────────── */}
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            {/* Big RAG pill */}
            <span
              data-testid="detail-signal-pill"
              style={{
                display:      "inline-flex",
                alignItems:   "center",
                padding:      "2px 10px",
                borderRadius: 6,
                flexShrink:   0,
                fontSize:     T.fsXs,
                fontWeight:   T.fwSemibold,
                background:   rollupStyle.bg,
                color:        rollupStyle.text,
              }}
            >
              {rollupStyle.pillLabel}
            </span>
            {/* Label + reason */}
            <div>
              <div style={{ fontSize: T.fsXxxs5, color: T.light, lineHeight: 1.4 }}>
                Capacity pressure
              </div>
              <div style={{ fontSize: T.fsXxs5, color: T.mid, lineHeight: 1.4, marginTop: 2 }}>
                {buildReasonString(market)}
              </div>
            </div>
          </div>
        </div>

        {/* ── Key figures ─────────────────────────────────────────────────── */}
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{
            fontSize:      T.fsXxxs5,
            fontWeight:    T.fwBold,
            color:         T.light,
            textTransform: "uppercase",
            letterSpacing: ".08em",
            marginBottom:  8,
          }}>
            Key figures
          </div>

          {/* Row 1: People · Usable seats · Peak */}
          <div style={{
            display:             "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap:                 5,
            marginBottom:        5,
          }}>
            <KpiTile
              testId="detail-kpi-people"
              label="People"
              value={totalPeople != null ? totalPeople.toLocaleString() : "—"}
              descriptor="current snapshot"
            />
            <KpiTile
              testId="detail-kpi-seats"
              label="Usable seats"
              value={totalSeats != null ? totalSeats.toLocaleString() : "—"}
              descriptor="installed · attendance-adj"
            />
            <KpiTile
              testId="detail-kpi-peak"
              label="Peak"
              value={peakPct != null ? `${peakPct}%` : "—"}
              descriptor="busiest vs seats"
            />
          </div>

          {/* Row 2: Gap · Next wall · Runway */}
          <div style={{
            display:             "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap:                 5,
          }}>
            <KpiTile
              testId="detail-kpi-gap"
              label="Gap"
              value={gapVal != null
                ? (gapVal < 0 ? String(gapVal) : `+${gapVal}`)
                : "—"}
              descriptor="seats short today"
            />
            <KpiTile
              testId="detail-kpi-next-wall"
              label="Next wall"
              value={computeNextWall(quarters)}
              descriptor="quarter or Now"
            />
            <KpiTile
              testId="detail-kpi-runway"
              label="Runway"
              value={computeRunway(quarters)}
              descriptor="months of runway"
            />
          </div>
        </div>

        {/* ── Offices section ─────────────────────────────────────────────── */}
        {officeCount > 0 && (
          <div
            data-testid="detail-panel-offices"
            style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}` }}
          >
            <div style={{
              fontSize:      T.fsXxxs5,
              fontWeight:    T.fwBold,
              color:         T.light,
              textTransform: "uppercase",
              letterSpacing: ".08em",
              marginBottom:  8,
            }}>
              Offices in this market
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {market.offices.map((office) => {
                const wv     = worstVerdictForOffice(office.cells);
                const oStyle = RAG_STYLE[wv] ?? RAG_STYLE.insufficient_history;
                return (
                  <div
                    key={office.locationId}
                    style={{
                      display:        "flex",
                      alignItems:     "center",
                      justifyContent: "space-between",
                      padding:        "5px 8px",
                      background:     T.lightGray,
                      borderRadius:   6,
                      fontSize:       T.fsXs,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: T.fwSemibold, color: T.ink }}>
                        {office.locationAlias}
                      </span>
                      {office.attendanceAsOfMonth != null && (
                        <span
                          data-testid={`detail-office-attendance-month-${office.locationId}`}
                          style={{ fontSize: T.fsXxs, color: T.light }}
                        >
                          {`As of ${fmtAttendanceMonth(office.attendanceAsOfMonth)}`}
                        </span>
                      )}
                      {office.oneTimeMoveDetected && (
                        <span
                          data-testid={`detail-one-time-badge-${office.locationId}`}
                          title="One-time move (consolidation or relocation): this headcount jump is excluded from the growth projection to avoid inflating the demand signal."
                          style={{
                            background:   T.blueBg,
                            color:        T.blue,
                            border:       `1px solid ${T.blue}`,
                            borderRadius: 10,
                            padding:      "1px 5px",
                            fontSize:     T.fsXxs,
                            fontWeight:   T.fwSemibold,
                          }}
                        >
                          one-time
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize:     T.fsXxs,
                        fontWeight:   T.fwSemibold,
                        padding:      "2px 7px",
                        borderRadius: 10,
                        background:   oStyle.bg,
                        color:        oStyle.text,
                        border:       `1px solid ${oStyle.border}`,
                      }}
                    >
                      {oStyle.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* One-time-move footnote */}
            {hasOneTimeMove && (
              <div
                data-testid="detail-one-time-move-note"
                style={{
                  fontSize:   T.fsXs,
                  color:      T.mid,
                  borderTop:  `1px solid ${T.border}`,
                  paddingTop: 10,
                  marginTop:  10,
                  lineHeight: 1.55,
                }}
              >
                <span
                  title="One-time move (consolidation or relocation): this headcount jump is excluded from the growth projection to avoid inflating the demand signal."
                  style={{
                    display:      "inline-block",
                    background:   T.blueBg,
                    color:        T.blue,
                    border:       `1px solid ${T.blue}`,
                    borderRadius: 12,
                    padding:      "1px 6px",
                    fontSize:     T.fsXxs,
                    fontWeight:   T.fwSemibold,
                    marginRight:  5,
                  }}
                >
                  one-time
                </span>
                A recent headcount spike was detected and excluded from the forward
                projection so it does not distort the growth-rate trend.
              </div>
            )}

            {/* Relocation stitching note */}
            {predecessorOffices.length > 0 && (
              <div
                data-testid="detail-predecessor-offices-note"
                style={{
                  fontSize:   T.fsXs,
                  color:      T.mid,
                  borderTop:  `1px solid ${T.border}`,
                  paddingTop: 10,
                  marginTop:  10,
                  lineHeight: 1.55,
                }}
              >
                {predecessorOffices.map((pred) => {
                  const range = formatMonthRange(pred.contributedMonths);
                  return (
                    <div
                      key={pred.locationId}
                      data-testid={`detail-predecessor-note-${pred.locationId}`}
                    >
                      Includes HC history from{" "}
                      <span style={{ fontWeight: T.fwSemibold, color: T.ink }}>
                        {pred.locationAlias}
                      </span>
                      {range ? ` (${range})` : ""}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Related section ─────────────────────────────────────────────── */}
        <div style={{ padding: "14px 18px" }}>
          <div style={{
            fontSize:      T.fsXxxs5,
            fontWeight:    T.fwBold,
            color:         T.light,
            textTransform: "uppercase",
            letterSpacing: ".08em",
            marginBottom:  8,
          }}>
            Related
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>

            {/* Market Plan card */}
            <div
              data-testid="detail-related-market-plan"
              onClick={() => {
                log.ui("MarketPressure: open market plan (related)", {
                  marketId: market.id,
                  layer,
                  basis,
                  horizon,
                });
                onOpenMarketPlan(market.id);
              }}
              style={{
                background:   T.hoverBg,
                border:       `1px solid ${T.border}`,
                borderRadius: 7,
                padding:      "9px 11px",
                cursor:       "pointer",
              }}
            >
              <div style={{ fontSize: T.fsXxs5, fontWeight: T.fwSemibold, color: T.ink }}>
                Market Plan → {market.name}
              </div>
              <div style={{ fontSize: T.fsXxxs5, color: T.light, marginTop: 2 }}>
                no disposition set · needs a plan
              </div>
            </div>

            {/* What-If card */}
            <div
              data-testid="detail-related-whatif"
              onClick={() => {
                log.ui("MarketPressure: test scenario (related)", {
                  marketId: market.id,
                  layer,
                  basis,
                  horizon,
                });
                onTestScenario(market.id);
              }}
              style={{
                background:   T.hoverBg,
                border:       `1px solid ${T.border}`,
                borderRadius: 7,
                padding:      "9px 11px",
                cursor:       "pointer",
              }}
            >
              <div style={{ fontSize: T.fsXxs5, fontWeight: T.fwSemibold, color: T.ink }}>
                What-If → test a fix
              </div>
              <div style={{ fontSize: T.fsXxxs5, color: T.light, marginTop: 2 }}>
                add seats or shift the team
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <div style={{
        padding:    "12px 18px",
        flexShrink: 0,
        display:    "flex",
        alignItems: "center",
        gap:        8,
        borderTop:  `1px solid ${T.border}`,
      }}>
        {/* Secondary: Test scenario */}
        <button
          data-testid="test-scenario-btn"
          type="button"
          onClick={() => {
            log.ui("MarketPressure: test scenario", { marketId: market.id, layer, basis, horizon });
            onTestScenario(market.id);
          }}
          style={{
            flex:         1,
            background:   T.card,
            color:        T.ink,
            border:       `1.5px solid ${T.border}`,
            borderRadius: 7,
            padding:      "8px 10px",
            fontSize:     T.fsSm5,
            fontWeight:   T.fwSemibold,
            cursor:       "pointer",
            textAlign:    "center",
          }}
        >
          Test scenario
        </button>
        {/* Primary: Open market plan */}
        <button
          data-testid="open-market-plan-btn"
          type="button"
          onClick={() => {
            log.ui("MarketPressure: open market plan", { marketId: market.id, layer, basis, horizon });
            onOpenMarketPlan(market.id);
          }}
          style={{
            flex:         1,
            background:   T.blue,
            color:        T.white,
            border:       0,
            borderRadius: 7,
            padding:      "8px 10px",
            fontSize:     T.fsSm5,
            fontWeight:   T.fwSemibold,
            cursor:       "pointer",
            textAlign:    "center",
          }}
        >
          Open market plan →
        </button>
      </div>

    </div>
  );
}
